import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { LogTransport, LogEntry, LogLevel } from '../interfaces/logger.interface';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class HttpTransport implements LogTransport {
  name = 'http';
  minLevel: LogLevel = LogLevel.INFO;

  private endpoint = '/api/logs';
  private isFailureLogged = false;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  setEndpoint(endpoint: string): void {
    this.endpoint = endpoint;
  }

  async log(entry: LogEntry): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      // For SSR, you might want to use a different approach
      // or queue the logs to be sent after hydration
      return;
    }

    try {
      await firstValueFrom(
        this.http.post(this.endpoint, entry, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
      );
      
      // Reset failure flag on successful log
      this.isFailureLogged = false;
    } catch (error) {
      // Only log the first failure to console to prevent infinite loop
      if (!this.isFailureLogged) {
        this.isFailureLogged = true;
        console.warn('HTTP logging transport failed. Subsequent failures will be silent to prevent loops.', {
          endpoint: this.endpoint,
          error: error instanceof Error ? error.message : String(error),
          status: (error as any)?.status
        });
        
        // Log rate limit info if available
        if ((error as any)?.status === 429) {
          console.warn('Logging rate limited. Consider reducing log frequency or adjusting rate limits.');
        }
      }
      
      // Don't re-throw to prevent retry logic that could make the loop worse
      // Let the log entry be silently dropped instead of cascading failures
    }
  }
}
